import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Selectors } from "../../../Everou/_reducers/app.reducer";
import { PMSLocationsActions } from "../../_actions/PMSLocationsActions";
import { VACATION_RENTAL_PLANS } from "../../_constants/vacationRentalPlan.constants";
import { CustomDropdown } from "../../../Common/_components/CustomDropdown";
import { CustomNumberInput } from "../../../Common/_components/CustomNumberInput";
import { FileUploadComponent, SimpleToggle } from "../../../Everou/_components";
import { PMSLocationEmbedDomains } from "./PMSLocationEmbedDomains";
import {
    InlineValidatedTextInput,
    SettingsRow,
    SettingsSection,
    SettingInheritanceStatus,
} from "./PMSLocationSettingsFields";

// Metres the guest may be off by before the public link refuses to open. Same
// bounds the backend validates: below the browser's own GPS error the radius
// would promise a precision the position doesn't have.
const LOCATION_RADIUS_MIN = 50;
const LOCATION_RADIUS_MAX = 10000;
const LOCATION_RADIUS_DEFAULT = 200;

// How strict the public link is about the guest's position when opening. Mirrors
// Location.INVITATION_LINK_LOCATION_CHECK_MODES in the backend.
const LOCATION_CHECK_MODES = {
    OFF: "off",
    WARN: "warn",
    REQUIRED: "required",
};

// The strict mode measures the distance from the location's coordinates, so it is
// only offered once the location has them.
const LOCATION_CHECK_MODE_OPTIONS = (t, hasCoordinates, currentMode) => [
    CustomDropdown.buildOption(
        LOCATION_CHECK_MODES.OFF,
        t("mod_pms_location_location_check_mode_off"),
    ),
    CustomDropdown.buildOption(
        LOCATION_CHECK_MODES.WARN,
        t("mod_pms_location_location_check_mode_warn"),
    ),
    // Offered with coordinates only — but when it is the saved mode it must
    // still be listed even without them, so the dropdown shows the real
    // state instead of rendering empty (the section tooltip already explains
    // why it cannot be chosen anew).
    ...(hasCoordinates || currentMode === LOCATION_CHECK_MODES.REQUIRED
        ? [CustomDropdown.buildOption(
            LOCATION_CHECK_MODES.REQUIRED,
            t("mod_pms_location_location_check_mode_required"),
        )] : []),
];

// Everything that configures the guest's access link. It used to live at the
// bottom of the Settings tab, which made that screen an endless scroll of
// unrelated rows; it belongs next to the device list of the link it configures.
export function PMSLocationPublicLinkSettings() {

    // LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // GLOBAL STATE
    const locationUid = useSelector(Selectors.getPMSLocationSelectedUid);
    const locationInfo = useSelector(Selectors.getPMSLocationInfo);

    function inheritedControl(field, serverField, control, resetAction = null) {
        return (
            <div>
                {control}
                <SettingInheritanceStatus
                    ownSettings={locationInfo.ownSettings}
                    field={field}
                    organizationName={locationInfo.settingsOrganizationName}
                    onReset={() => resetAction
                        ? resetAction()
                        : dispatch(
                            PMSLocationsActions.clearOrganizationSettingOverride(
                                serverField,
                                locationInfo.uid,
                            ),
                        )}
                />
            </div>
        );
    }

    // LOCAL STATE
    const [isUploadingVideo, setIsUploadingVideo] = useState(false);
    const [showVideoPreview, setShowVideoPreview] = useState(false);
    const [addToHomeLocal, setAddToHomeLocal] = useState(locationInfo.invitationLinkAddToHome);
    const [appNameLocal, setAppNameLocal] = useState(locationInfo.invitationLinkAppName);
    const [earlyCheckinLocal, setEarlyCheckinLocal] = useState(locationInfo.earlyCheckinEnabled);
    const [earlyCheckoutLocal, setEarlyCheckoutLocal] = useState(locationInfo.earlyCheckoutEnabled);
    const [locationCheckModeLocal, setLocationCheckModeLocal] = useState(locationInfo.invitationLinkLocationCheckMode);
    const [locationRadiusLocal, setLocationRadiusLocal] = useState(
        locationInfo.invitationLinkLocationRadius ?? LOCATION_RADIUS_DEFAULT,
    );
    // Typing or stepping the metres coalesces into a single write instead of
    // one per keystroke. The pending payload travels with the timer (metres AND
    // the location they belong to) so leaving the screen can flush it without
    // posting to whichever location is open by then.
    const locationRadiusSaveTimer = useRef(null);
    const locationRadiusPendingSave = useRef(null);
    // The write is optimistic and never re-fetches, so the value in the store
    // stays the one loaded on mount: what tells us whether a write is needed is
    // the last value we sent, not the one the location was fetched with.
    const locationRadiusSavedValue = useRef(
        locationInfo.invitationLinkLocationRadius ?? LOCATION_RADIUS_DEFAULT,
    );

    // INITIALIZATION
    useEffect(() => {
        setAddToHomeLocal(locationInfo.invitationLinkAddToHome);
    }, [locationInfo.invitationLinkAddToHome]);

    useEffect(() => {
        setAppNameLocal(locationInfo.invitationLinkAppName);
    }, [locationInfo.invitationLinkAppName]);

    useEffect(() => {
        setEarlyCheckinLocal(locationInfo.earlyCheckinEnabled);
    }, [locationInfo.earlyCheckinEnabled]);

    useEffect(() => {
        setEarlyCheckoutLocal(locationInfo.earlyCheckoutEnabled);
    }, [locationInfo.earlyCheckoutEnabled]);

    useEffect(() => {
        setLocationCheckModeLocal(locationInfo.invitationLinkLocationCheckMode);
    }, [locationInfo.invitationLinkLocationCheckMode]);

    useEffect(() => {
        const radius =
            locationInfo.invitationLinkLocationRadius ?? LOCATION_RADIUS_DEFAULT;
        setLocationRadiusLocal(radius);
        locationRadiusSavedValue.current = radius;
    }, [locationInfo.invitationLinkLocationRadius]);

    // The debounced radius write is in flight for up to 600 ms, and switching
    // tab unmounts this screen well inside that window: flush it instead of
    // dropping it, or a change made right before leaving is lost in silence.
    useEffect(() => () => {
        if (locationRadiusSaveTimer.current)
            clearTimeout(locationRadiusSaveTimer.current);

        const pending = locationRadiusPendingSave.current;
        if (!pending)
            return;

        dispatch(PMSLocationsActions.updateInvitationLinkLocationRadius(
            pending.metres,
            pending.uid,
        ));
    }, [dispatch]);

    // ACTION HANDLERS
    async function handleUploadVideo(video) {
        setIsUploadingVideo(true);
        try {
            await dispatch(PMSLocationsActions.uploadVideo(video, locationInfo.uid));
        } finally {
            setIsUploadingVideo(false);
        }
    }

    async function handleDeleteVideo() {
        setIsUploadingVideo(true);
        try {
            await dispatch(PMSLocationsActions.deleteVideo(locationInfo.uid));
            setShowVideoPreview(false);
        } finally {
            setIsUploadingVideo(false);
        }
    }

    // VIEW
    // BUSINESS-only settings, so hide them — title included — for any lower
    // plan.
    if (locationInfo.vacationRentalPlan !== VACATION_RENTAL_PLANS.PREMIUM_PLAN)
        return null;

    // Every setting is one row: an uppercase title per single toggle made this
    // tab three screens long, and each title said the same as the row under it.
    return (
        <div>
            <SettingsSection title={t("mod_pms_location_public_link_section_title")}>
                {invitationLinkAddToHomeRows()}
                {locationCheckRows()}
                {recommendationsRow()}
                {videoRow()}
            </SettingsSection>
            {/* The two stay settings get their own section: they are what the
                guest can ask for from the link, and buried among the rest they
                read as fine print. */}
            <SettingsSection title={t("mod_pms_location_stay_section_title")}>
                {earlyCheckinRow()}
                {earlyCheckoutRow()}
            </SettingsSection>
            <SettingsSection>
                <PMSLocationEmbedDomains />
            </SettingsSection>
        </div>
    );

    function recommendationsRow() {

        //ACTION HANDLERS
        function handleSaveURL(text) {
            dispatch(PMSLocationsActions.updateURLRecommendations(text, locationUid));
        }

        return (
            <SettingsRow
                label={t("mod_pms_location_service_recommendations_setting_title")}
                tooltip={t("mod_pms_location_public_link_section_tooltip")}
            >
                {inheritedControl("urlRecommendations", "url_recommendations", <InlineValidatedTextInput
                    handleSave={handleSaveURL}
                    initialValue={locationInfo.urlRecommendations}
                    required={false}
                    className="flex-grow-1 ml-3"
                    inputClassName="flex-grow-1"
                />)}
            </SettingsRow>
        );
    }

    // The video is part of what the access link shows the guest, so it is one
    // more row of that section instead of a section of its own.
    function videoRow() {

        //ACTION HANDLERS
        function handleSaveVideoURL(text) {
            dispatch(PMSLocationsActions.updateURLVideo(text, locationUid));
        }

        return (
            <SettingsRow
                label={t("mod_pms_location_video_title")}
                tooltip={t("mod_pms_location_video_section_tooltip")}
            >
                {/* The uploader and the URL are two ways to set the same thing,
                    so they share one line. Aligned to the top because the
                    uploader grows into a box (name, preview, replace) once a
                    video is stored, while the URL stays one field tall. */}
                <div
                    className="d-flex align-items-start flex-grow-1 ml-3"
                    style={{ columnGap: "12px" }}
                >
                    <div style={{ flex: "1 1 0", minWidth: 0 }}>
                        <FileUploadComponent
                            onUpload={handleUploadVideo}
                            isUploading={isUploadingVideo}
                            fileType=".mp4,.webm,video/mp4,video/webm"
                            fileName={locationInfo.videoUrl ? t("mod_pms_location_video_uploaded") : null}
                            onRemove={handleDeleteVideo}
                            actions={
                                <span
                                    className="cp c-text-primary c-font-weight-medium"
                                    onClick={() => setShowVideoPreview(v => !v)}
                                >
                                    {showVideoPreview
                                        ? t("mod_pms_location_video_hide")
                                        : t("mod_pms_location_video_show")
                                    }
                                </span>
                            }
                            preview={showVideoPreview &&
                                <video
                                    key={locationInfo.videoUrl}
                                    src={locationInfo.videoUrl}
                                    controls
                                    className="w-100 c-rounded-big bg-dark"
                                    style={{ maxHeight: "15rem" }}
                                />
                            }
                        />
                        <small className="d-block mt-1 text-secondary">
                            {t("mod_pms_location_video_requirements")}
                        </small>
                        <SettingInheritanceStatus
                            align="start"
                            ownSettings={locationInfo.ownSettings}
                            field="videoUrl"
                            organizationName={locationInfo.settingsOrganizationName}
                            onReset={handleDeleteVideo}
                        />
                    </div>
                    <div
                        style={{ flex: "1 1 0", minWidth: 0 }}
                    >
                        <div className="d-flex align-items-center">
                            <span className="text-secondary mr-2 flex-shrink-0">
                                {t("mod_pms_location_video_or_url")}
                            </span>
                            <InlineValidatedTextInput
                                handleSave={handleSaveVideoURL}
                                initialValue={locationInfo.externalVideoUrl}
                                required={false}
                                className="flex-grow-1"
                                inputClassName="flex-grow-1"
                            />
                        </div>
                        <SettingInheritanceStatus
                            align="start"
                            ownSettings={locationInfo.ownSettings}
                            field="externalVideoUrl"
                            organizationName={locationInfo.settingsOrganizationName}
                            onReset={() => dispatch(
                                PMSLocationsActions.clearOrganizationSettingOverride(
                                    "external_video_url",
                                    locationInfo.uid,
                                ),
                            )}
                        />
                    </div>
                </div>
            </SettingsRow>
        );
    }

    function invitationLinkAddToHomeRows() {

        //ACTION HANDLERS
        function handleChange(value) {
            setAddToHomeLocal(value);
            dispatch(PMSLocationsActions.updateInvitationLinkAddToHome(value, locationUid));
        }

        function handleSaveAppName(text) {
            setAppNameLocal(text);
            dispatch(PMSLocationsActions.updateInvitationLinkAppName(text, locationUid));
        }

        return (
            <>
                <SettingsRow
                    label={t("mod_pms_location_invitation_link_add_to_home_title")}
                    tooltip={t("mod_pms_location_invitation_link_section_tooltip")}
                >
                    {inheritedControl("invitationLinkAddToHome", "invitation_link_add_to_home", <SimpleToggle
                        className="c-toggle-smooth"
                        checked={addToHomeLocal}
                        handleChange={({ detail: { value } }) => handleChange(value)}
                    />)}
                </SettingsRow>
                {addToHomeLocal &&
                    <SettingsRow label={t("mod_pms_location_invitation_link_app_name_title")}>
                        {inheritedControl("invitationLinkAppName", "invitation_link_app_name", <InlineValidatedTextInput
                            key={appNameLocal}
                            handleSave={handleSaveAppName}
                            initialValue={appNameLocal}
                            placeholder="Tripkey"
                            required={false}
                            maxLength={12}
                        />)}
                    </SettingsRow>
                }
            </>
        );
    }

    function locationCheckRows() {

        // Requiring the position is measured against the location's own coordinates,
        // so the server refuses it while there are none: the option is not offered,
        // and the tooltip says why rather than adding a row to explain it.
        const hasCoordinates =
            locationInfo.latitude != null && locationInfo.longitude != null;

        //ACTION HANDLERS
        function handleChangeLocationCheckMode(value) {
            if (value === locationCheckModeLocal) return;
            setLocationCheckModeLocal(value);
            dispatch(PMSLocationsActions.updateInvitationLinkLocationCheckMode(value, locationInfo.uid));
        }

        function clampRadius(metres) {
            if (!metres || isNaN(metres))
                return LOCATION_RADIUS_MIN;

            return Math.min(
                Math.max(Math.round(metres), LOCATION_RADIUS_MIN),
                LOCATION_RADIUS_MAX,
            );
        }

        function saveRadius(metres) {
            if (locationRadiusSaveTimer.current) {
                clearTimeout(locationRadiusSaveTimer.current);
                locationRadiusSaveTimer.current = null;
                locationRadiusPendingSave.current = null;
            }

            // Nothing to write when the value is already the saved one: the
            // input reports a blur even when it was only focused and left, and
            // typing back to the last saved value would otherwise still post.
            if (metres === locationRadiusSavedValue.current)
                return;

            locationRadiusPendingSave.current = { metres, uid: locationInfo.uid };
            locationRadiusSaveTimer.current = setTimeout(() => {
                locationRadiusSaveTimer.current = null;
                locationRadiusPendingSave.current = null;
                locationRadiusSavedValue.current = metres;
                dispatch(PMSLocationsActions.updateInvitationLinkLocationRadius(metres, locationInfo.uid));
            }, 600);
        }

        function handleChangeRadius(metres) {
            setLocationRadiusLocal(metres);
            saveRadius(clampRadius(metres));
        }

        // Normalize a cleared/out-of-range field on blur so what is shown is what
        // was saved — the backend refuses anything outside the bounds.
        function handleBlurRadius(metres) {
            const clamped = clampRadius(metres);
            setLocationRadiusLocal(clamped);
            saveRadius(clamped);
        }

        // The radius is what the chosen mode measures against, so both controls
        // share one row: on separate rows the radius read as an unrelated
        // setting that happened to appear and disappear on its own.
        return (
            <SettingsRow
                label={t("mod_pms_location_location_check_mode_title")}
                tooltip={t(hasCoordinates
                    ? "mod_pms_location_location_check_mode_section_tooltip"
                    : "mod_pms_location_location_check_mode_section_tooltip_no_coordinates")}
            >
                {/* Two controls on one line, so each carries its own
                    provenance caption under itself: a single caption for the
                    row sat by the radius and read as if it described it. */}
                <div className="d-flex align-items-start">
                    <div style={{ width: "15rem" }}>
                        <CustomDropdown
                            value={locationCheckModeLocal}
                            onChange={handleChangeLocationCheckMode}
                            options={LOCATION_CHECK_MODE_OPTIONS(t, hasCoordinates, locationCheckModeLocal)}
                            mode="to-left"
                            isSearchable={false}
                        />
                        <SettingInheritanceStatus
                            align="start"
                            ownSettings={locationInfo.ownSettings}
                            field="invitationLinkLocationCheckMode"
                            organizationName={locationInfo.settingsOrganizationName}
                            onReset={() => dispatch(
                                PMSLocationsActions.clearOrganizationSettingOverride(
                                    "invitation_link_location_check_mode",
                                    locationInfo.uid,
                                ),
                            )}
                        />
                    </div>
                    {/* Both the warning and the strict mode measure against this
                        radius, so it only disappears when nothing is checked. */}
                    {locationCheckModeLocal !== LOCATION_CHECK_MODES.OFF &&
                        <div style={{ marginLeft: "2rem" }}>
                            <div className="d-flex align-items-center">
                                <span className="text-secondary mr-2 flex-shrink-0">
                                    {t("mod_pms_location_location_radius_title")}
                                </span>
                            {/* min={1}, not the real 50: CustomNumberInput
                                rewrites the field to `min` on every keystroke,
                                so a floor of 50 turns "800" into 5000 as it is
                                typed. The real bounds are applied when saving
                                and on blur. */}
                            <CustomNumberInput
                                value={locationRadiusLocal}
                                min={1}
                                max={LOCATION_RADIUS_MAX}
                                onChange={handleChangeRadius}
                                onBlur={handleBlurRadius}
                            />
                            </div>
                            <SettingInheritanceStatus
                                align="start"
                                ownSettings={locationInfo.ownSettings}
                                field="invitationLinkLocationRadius"
                                organizationName={locationInfo.settingsOrganizationName}
                                onReset={() => dispatch(
                                    PMSLocationsActions.clearOrganizationSettingOverride(
                                        "invitation_link_location_radius",
                                        locationInfo.uid,
                                    ),
                                )}
                            />
                        </div>
                    }
                </div>
            </SettingsRow>
        );
    }

    function earlyCheckinRow() {

        //ACTION HANDLERS
        function handleChangeEarlyCheckin(value) {
            setEarlyCheckinLocal(value);
            dispatch(PMSLocationsActions.updateEarlyCheckinEnabled(value, locationInfo.uid));
        }

        return (
            <SettingsRow
                label={t("mod_pms_location_early_checkin_section_title")}
                tooltip={t("mod_pms_location_early_checkin_section_tooltip")}
            >
                {inheritedControl("earlyCheckinEnabled", "early_checkin_enabled", <SimpleToggle
                    className="c-toggle-smooth"
                    checked={earlyCheckinLocal}
                    handleChange={({ detail: { value } }) => handleChangeEarlyCheckin(value)}
                />)}
            </SettingsRow>
        );
    }

    function earlyCheckoutRow() {

        //ACTION HANDLERS
        function handleChangeEarlyCheckout(value) {
            setEarlyCheckoutLocal(value);
            dispatch(PMSLocationsActions.updateEarlyCheckoutEnabled(value, locationInfo.uid));
        }

        return (
            <SettingsRow
                label={t("mod_pms_location_early_checkout_section_title")}
                tooltip={t("mod_pms_location_early_checkout_section_tooltip")}
            >
                {inheritedControl("earlyCheckoutEnabled", "early_checkout_enabled", <SimpleToggle
                    className="c-toggle-smooth"
                    checked={earlyCheckoutLocal}
                    handleChange={({ detail: { value } }) => handleChangeEarlyCheckout(value)}
                />)}
            </SettingsRow>
        );
    }
}
